import { useState, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, X, Copy, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const ALLOWED_EXT = [".csv", ".xlsx", ".xls"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dateFromName = (name) => {
  const re = /(?<!\d)(\d{4})[-_.](\d{1,2})[-_.](\d{1,2})(?!\d)/g;
  let m;
  while ((m = re.exec(name))) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d) {
      return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  return null;
};

const fmtDate = (v) => {
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const countRows = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        resolve(rows.length);
      } catch {
        resolve(null);
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file);
  });

const Stat = ({ value, label, color, testId }) => (
  <div className="text-center min-w-[64px]">
    <div className={`text-lg font-bold tabular-nums ${color}`} data-testid={testId}>{value.toLocaleString()}</div>
    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
  </div>
);

const FileRow = ({ item, index, onRemove, disabled }) => {
  const r = item.result;
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 border rounded-lg" data-testid={`upload-file-row-${index}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <FileSpreadsheet className="w-8 h-8 text-primary shrink-0" />
        <div className="min-w-0">
          <p className="font-medium truncate" data-testid={`upload-file-name-${index}`}>{item.file.name}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <span>{(item.file.size / 1024).toFixed(1)} KB</span>
            {item.rows !== null && <span>• {item.rows.toLocaleString()} row{item.rows === 1 ? "" : "s"}</span>}
            <span className="inline-flex items-center gap-1" data-testid={`upload-file-date-${index}`}>
              <CalendarDays className="w-3 h-3" />
              {fmtDate(r?.uploaded_at || item.date || todayStr())}
              <span className="opacity-70">{item.date ? "(from file name)" : "(today)"}</span>
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 md:justify-end">
        {item.status === "uploading" && (
          <Badge variant="outline" data-testid={`upload-file-status-${index}`}><Loader2 className="w-3 h-3 mr-1 animate-spin" />Uploading</Badge>
        )}
        {item.status === "error" && (
          <Badge variant="outline" className="text-destructive border-destructive/40" data-testid={`upload-file-status-${index}`} title={item.error}>
            <AlertCircle className="w-3 h-3 mr-1" />{item.error || "Failed"}
          </Badge>
        )}
        {item.status === "pending" && <Badge variant="outline" data-testid={`upload-file-status-${index}`}>Ready</Badge>}
        {r && (
          <div className="flex gap-2" data-testid={`upload-file-stats-${index}`}>
            <Stat value={r.total_records} label="Total" color="text-foreground" testId={`stat-total-${index}`} />
            <Stat value={r.unique_records} label="Unique" color="text-green-500" testId={`stat-unique-${index}`} />
            <Stat value={r.internal_duplicates} label="Internal" color="text-orange-500" testId={`stat-internal-${index}`} />
            <Stat value={r.historical_duplicates} label="Historical" color="text-red-500" testId={`stat-historical-${index}`} />
          </div>
        )}
        {!disabled && item.status === "pending" && (
          <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`remove-file-btn-${index}`}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

const DropZone = ({ onFiles, dragActive, setDragActive, disabled }) => {
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    handleDrag(e);
    setDragActive(false);
    if (!disabled) onFiles(e.dataTransfer.files);
  };
  return (
    <label
      htmlFor="file-input"
      className={`tracing-beam border-2 border-dashed rounded-xl p-10 text-center transition-colors block ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${
        dragActive ? "dropzone-active border-primary bg-primary/5" : "border-border hover:border-primary/50"
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      data-testid="upload-dropzone"
    >
      <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Upload className="w-7 h-7 text-primary" />
      </div>
      <h3 className="text-lg font-semibold mb-1">Drag & drop one or more CSV / Excel files</h3>
      <p className="text-muted-foreground mb-4 text-sm">or click to browse — you can select multiple files</p>
      <input
        type="file"
        multiple
        disabled={disabled}
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
        className="hidden"
        id="file-input"
        data-testid="file-input"
      />
      <span className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="browse-files-btn">
        Browse Files
      </span>
      <p className="text-xs text-muted-foreground mt-4">
        Supported formats: CSV, Excel (.xlsx, .xls). A date in the file name (e.g. leads_2026-06-15.csv) is used as the upload date.
      </p>
    </label>
  );
};

const UploadLeads = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    const valid = files.filter((f) => ALLOWED_EXT.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (valid.length < files.length) toast.error("Some files were skipped — only CSV or Excel (.xlsx, .xls) allowed");
    const newItems = await Promise.all(
      valid.map(async (file) => ({ id: `${Date.now()}-${Math.random()}`, file, rows: await countRows(file), date: dateFromName(file.name), status: "pending", result: null, error: null }))
    );
    setItems((prev) => [...prev, ...newItems]);
  }, []);

  const updateItem = (id, patch) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const uploadAll = async () => {
    setUploading(true);
    let ok = 0;
    for (const item of items.filter((it) => it.status === "pending")) {
      const i = item.id;
      updateItem(i, { status: "uploading" });
      try {
        const formData = new FormData();
        formData.append("file", item.file);
        const res = await fetch(`${API}/leads/upload`, { method: "POST", credentials: "include", body: formData });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          ok++;
          updateItem(i, { status: "done", result: data });
        } else {
          updateItem(i, { status: "error", error: typeof data.detail === "string" ? data.detail : `Error ${res.status}` });
        }
      } catch {
        updateItem(i, { status: "error", error: "Network error" });
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded`);
  };

  if (user?.role === "Viewer") {
    return (
      <Layout title="Upload Leads" subtitle="Import leads from CSV or Excel files">
        <Card className="border-border/60">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Permission Required</h2>
            <p className="text-muted-foreground">You don't have permission to upload leads. Please contact an administrator.</p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const pending = items.filter((i) => i.status === "pending").length;
  const done = items.filter((i) => i.result);
  const sum = (k) => done.reduce((a, i) => a + i.result[k], 0);

  return (
    <Layout title="Upload Leads" subtitle="Import leads from CSV or Excel files">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="border-border/60">
          <CardContent className="p-6">
            <DropZone onFiles={addFiles} dragActive={dragActive} setDragActive={setDragActive} disabled={uploading} />
          </CardContent>
        </Card>

        {items.length > 0 && (
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold font-['Manrope']" data-testid="upload-files-count">
                Files ({items.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map((item, i) => (
                <FileRow key={item.id} item={item} index={i} disabled={uploading} onRemove={() => setItems((p) => p.filter((x) => x.id !== item.id))} />
              ))}

              {done.length > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-muted/40" data-testid="upload-totals">
                  <span className="font-semibold flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />All files total</span>
                  <div className="flex gap-2">
                    <Stat value={sum("total_records")} label="Total" color="text-foreground" testId="total-all" />
                    <Stat value={sum("unique_records")} label="Unique" color="text-green-500" testId="unique-all" />
                    <Stat value={sum("internal_duplicates")} label="Internal" color="text-orange-500" testId="internal-all" />
                    <Stat value={sum("historical_duplicates")} label="Historical" color="text-red-500" testId="historical-all" />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3 pt-2">
                {!uploading && (
                  <Button variant="outline" onClick={() => setItems([])} data-testid="clear-files-btn">Clear</Button>
                )}
                {done.length > 0 && !uploading && (
                  <>
                    <Button variant="outline" onClick={() => navigate("/duplicates")} data-testid="view-duplicates-btn">
                      <Copy className="w-4 h-4 mr-2" />View Duplicates
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/leads")} data-testid="view-all-leads-btn">View All Leads</Button>
                  </>
                )}
                {pending > 0 && (
                  <Button onClick={uploadAll} disabled={uploading} className="active-scale min-w-[180px]" data-testid="upload-leads-btn">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {uploading ? "Uploading..." : `Upload ${pending} File${pending > 1 ? "s" : ""}`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default UploadLeads;
